import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import alertify from 'alertifyjs';
import { LandingNavbarComponent } from '../../components/landing-navbar/landing-navbar.component';
import { AuthHeroSectionComponent } from '../../components/auth-hero-section/auth-hero-section.component';
import { ApiServiceService } from '../../modules/services/api-service.service';
import {
  CreateUserRequest,
  RegistrationResponse,
  RegistrationStep,
  ValidateAsinData,
  ValidateAsinResponse,
} from './registration-model';

@Component({
  selector: 'app-registration-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    LandingNavbarComponent,
    AuthHeroSectionComponent,
  ],
  templateUrl: './registration-page.html',
  styleUrl: './registration-page.scss',
})
export class RegistrationPage implements AfterViewInit, OnDestroy {
  step: RegistrationStep = 'validate';
  readonly registrationForm;
  isSubmitting = false;
  isSuccessModalOpen = false;
  submittedEmail = '';
  private validatedAsinData: ValidateAsinData | null = null;
  private intersectionObserver: IntersectionObserver | null = null;

  constructor(
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly apiService: ApiServiceService,
    private readonly router: Router,
    private readonly host: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
  ) {
    alertify.set('notifier', 'position', 'top-right');
    alertify.set('notifier', 'delay', 4);

    this.registrationForm = this.formBuilder.group({
      asin: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      vendorName: ['', Validators.required],
      phoneNumber: ['', Validators.required],
      address: ['', Validators.required],
    });
  }

  ngAfterViewInit(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting)
            this.renderer.addClass(entry.target, 'visible');
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
    );

    const revealEls = this.host.nativeElement.querySelectorAll(
      '.auth-scroll-reveal',
    );
    revealEls.forEach((el) => this.intersectionObserver?.observe(el));
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;
  }

  validateAsin(): void {
    if (this.asinControl.invalid) {
      this.asinControl.markAsTouched();
      return;
    }

    const asin = this.asinControl.value.trim();
    if (!asin) {
      this.asinControl.markAsTouched();
      return;
    }

    this.isSubmitting = true;

    this.apiService.validateAsin(asin).subscribe({
      next: (response: ValidateAsinResponse) => {
        if (response.status !== 'success' || !response.data) {
          alertify.error(
            response.message || 'We could not validate this ASIN.',
          );
          this.isSubmitting = false;
          return;
        }

        const { data } = response;
        if (data.taxGroup !== 'COMPANY') {
          alertify.error('Company ASIN is required');
          this.isSubmitting = false;
          return;
        }

        this.validatedAsinData = data;
        const vendorName = [data.firstName, data.middleName, data.lastName]
          .filter(Boolean)
          .join(' ');

        this.registrationForm.patchValue({
          asin: data.asin || asin,
          email: data.email || '',
          vendorName,
          phoneNumber: data.phone || '',
          address: data.address || '',
        });

        this.step = 'details';
        this.isSubmitting = false;
        alertify.success(response.message || 'ASIN validated successfully.');
      },
      error: (error) => {
        const message =
          typeof error === 'string'
            ? error
            : 'We could not validate this ASIN. Please try again.';
        alertify.error(message);
        this.isSubmitting = false;
      },
    });
  }

  get isDetailsStep(): boolean {
    return this.step === 'details';
  }

  get asinControl() {
    return this.registrationForm.controls.asin;
  }

  get emailControl() {
    return this.registrationForm.controls.email;
  }

  get vendorNameControl() {
    return this.registrationForm.controls.vendorName;
  }

  get phoneNumberControl() {
    return this.registrationForm.controls.phoneNumber;
  }

  get addressControl() {
    return this.registrationForm.controls.address;
  }

  get asinErrorMessage(): string {
    if (!this.asinControl.touched && !this.asinControl.dirty) return '';
    if (this.asinControl.hasError('required'))
      return 'Company ASIN is required.';
    if (this.asinControl.hasError('pattern')) return 'Enter a valid ASIN.';
    return '';
  }

  get emailErrorMessage(): string {
    if (!this.emailControl.touched && !this.emailControl.dirty) return '';
    if (this.emailControl.hasError('required')) {
      return 'Email address is required.';
    }
    if (this.emailControl.hasError('email')) {
      return 'Enter a valid email address.';
    }
    return '';
  }

  get canValidateAsin(): boolean {
    return this.asinControl.valid && !this.isSubmitting;
  }

  hasValidatedValue(field: keyof ValidateAsinData): boolean {
    const value = this.validatedAsinData?.[field];
    return typeof value === 'string' && value.trim().length > 0;
  }

  get hasValidatedVendorName(): boolean {
    return Boolean(
      this.hasValidatedValue('firstName') ||
      this.hasValidatedValue('middleName') ||
      this.hasValidatedValue('lastName'),
    );
  }

  get canSubmit(): boolean {
    return (
      this.isDetailsStep && this.registrationForm.valid && !this.isSubmitting
    );
  }

  goBackToAsinVerification(): void {
    if (this.isSubmitting) return;

    this.step = 'validate';
  }

  submit(): void {
    if (!this.isDetailsStep) {
      this.validateAsin();
      return;
    }

    if (this.registrationForm.invalid) {
      this.registrationForm.markAllAsTouched();
      alertify.error('Complete the registration form before submitting.');
      return;
    }

    const formValue = this.registrationForm.getRawValue();
    const payload: CreateUserRequest = {
      first_name: this.validatedAsinData?.firstName || '',
      last_name: this.validatedAsinData?.lastName || '',
      middle_name: this.validatedAsinData?.middleName || '',
      email: formValue.email,
      date_of_birth: '',
      gender: '',
      asin: formValue.asin,
      phone_number: formValue.phoneNumber,
      state: 0,
      lga: 0,
      area: 0,
      address: formValue.address,
    };

    this.isSubmitting = true;

    this.apiService.post('/create-user', payload).subscribe({
      next: (response: RegistrationResponse) => {
        if (response.status && response.status !== 'success') {
          alertify.error(
            response.message || 'We could not create your account.',
          );
          this.isSubmitting = false;
          return;
        }

        this.submittedEmail = formValue.email;
        this.isSuccessModalOpen = true;
        this.isSubmitting = false;
      },
      error: (error) => {
        const message =
          typeof error === 'string'
            ? error
            : 'We could not create your account. Please try again.';
        alertify.error(message);
        this.isSubmitting = false;
      },
    });
  }

  closeSuccessModal(): void {
    this.isSuccessModalOpen = false;
    void this.router.navigate(['/login']);
  }
}
